import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  FilledInput,
  InputLabel,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  Avatar,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import LockIcon from '@material-ui/icons/Lock';
import { toast } from 'react-toastify';

import { ApplicationState } from '../../../store';
import api from '../../../services/api';
import { changeUser } from '../../../store/modules/user/actions';
import { phoneMask } from '../../../utils/functions';
import { Container, Section, SectionTitle, useStyles } from './AccountStyles';
import { logOut } from '../../../store/modules/auth/actions';

export default function Account() {
  const dispatch = useDispatch();
  const userState = useSelector((state: ApplicationState) => state.user);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState<any>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get('/billing/status').then(res => {
      setBillingStatus(res.data);
    }).catch(() => {});
  }, []);

  function handleManageSubscription() {
    setPortalLoading(true);
    api.post('/billing/portal').then(res => {
      setPortalLoading(false);
      window.open(res.data.portalUrl, '_blank');
    }).catch(err => {
      setPortalLoading(false);
      toast.error((err && err.response && err.response.data && err.response.data.error) || 'Erro ao abrir portal');
    });
  }

  const [name, setName] = useState(userState.user.name);
  const [phone, setPhone] = useState(phoneMask(userState.user.phone || ''));
  const [goal, setGoal] = useState(userState.user.config.goalOfMonth);
  const [currency, setCurrency] = useState(userState.user.config.currency);
  const [stopWin, setStopWin] = useState(userState.user.config.stopWin || 0);
  const [stopLoss, setStopLoss] = useState(userState.user.config.stopLoss || 0);

  const [currentPassword, setCurrentPassword] = useState({ value: '', showPassword: false });
  const [newPassword, setNewPassword] = useState({ value: '', showPassword: false });
  const [confirmNewPassword, setconfirmNewPassword] = useState({ value: '', showPassword: false });

  function handleSubmit() {
    setLoading(true);
    const cleanPhone = phone.replace(/\D/g, '');
    const payload: any = { name, phone: cleanPhone, goalOfMonth: goal, currency, stopWin, stopLoss };
    if (currentPassword.value) {
      payload.oldPassword = currentPassword.value;
      payload.password = newPassword.value;
      payload.confirmPassword = confirmNewPassword.value;
    }
    api.put('user', payload)
      .then(res => {
        setLoading(false);
        dispatch(changeUser(name, cleanPhone, goal, currency, stopWin, stopLoss));
        toast.success(res.data.message);
      })
      .catch(err => {
        setLoading(false);
        toast.error(err.response.data.error);
      });
    setCurrentPassword({ value: '', showPassword: false });
    setNewPassword({ value: '', showPassword: false });
    setconfirmNewPassword({ value: '', showPassword: false });
  }

  const regInitials = userState.user.name.match(/\b\w/g) || [];
  const initials = ((regInitials.shift() || '') + (regInitials.pop() || '')).toUpperCase();
  const usagePercent = billingStatus && billingStatus.tradeLimit > 0
    ? Math.min((billingStatus.tradeCount / billingStatus.tradeLimit) * 100, 100)
    : 0;

  return (
    <Container>
      {/* Header */}
      <div style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Avatar style={{
            width: '48px',
            height: '48px',
            background: '#1E2141',
            color: '#ffee58',
            fontWeight: 700,
            fontSize: '18px',
          }}>
            {initials}
          </Avatar>
          <div>
            <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.2 }}>
              {userState.user.name}
            </Typography>
            <Typography variant="body2" style={{ color: '#ffffff77', fontSize: '13px' }}>
              {userState.user.email}
            </Typography>
          </div>
        </div>
        <Button
          className={classes.logOut}
          onClick={() => dispatch(logOut())}
          startIcon={<ExitToAppIcon style={{ fontSize: '18px' }} />}
        >
          Sair
        </Button>
      </div>

      {/* Subscription */}
      {billingStatus && (
        <Section>
          <SectionTitle>
            <CreditCardIcon style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '8px', marginTop: '-2px' }} />
            Assinatura
          </SectionTitle>
          {billingStatus.status === 'none' || billingStatus.plan === 'free' ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Typography variant="body2" style={{ color: '#ffffff99', marginBottom: '16px' }}>
                Você ainda não possui uma assinatura ativa.
              </Typography>
              <Link to="/plans" style={{ textDecoration: 'none' }}>
                <Button variant="contained" color="primary" style={{ color: '#1E2141', fontWeight: 700, padding: '8px 32px' }}>
                  Escolher plano
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#1E2141',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
              }}>
                <div>
                  <Typography variant="body2" style={{ color: '#ffffff77', fontSize: '12px', marginBottom: '2px' }}>
                    Plano
                  </Typography>
                  <Typography style={{ color: '#ffee58', fontWeight: 700, fontSize: '18px' }}>
                    {billingStatus.plan === 'basic' ? 'Básico' : billingStatus.plan === 'pro' ? 'Pro' : billingStatus.plan === 'premium' ? 'Premium' : billingStatus.plan}
                  </Typography>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Typography variant="body2" style={{ color: '#ffffff77', fontSize: '12px', marginBottom: '2px' }}>
                    Status
                  </Typography>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: billingStatus.status === 'active' ? '#4BDC4B22' : '#FF634722',
                    color: billingStatus.status === 'active' ? '#4BDC4B' : '#FF6347',
                  }}>
                    {billingStatus.status === 'active' ? 'Ativo' : billingStatus.status === 'past_due' ? 'Pendente' : billingStatus.status}
                  </span>
                </div>
                {billingStatus.currentPeriodEnd && (
                  <div style={{ textAlign: 'right' }}>
                    <Typography variant="body2" style={{ color: '#ffffff77', fontSize: '12px', marginBottom: '2px' }}>
                      Próxima cobrança
                    </Typography>
                    <Typography variant="body2" style={{ color: '#ffffffcc', fontWeight: 600 }}>
                      {new Date(billingStatus.currentPeriodEnd).toLocaleDateString('pt-BR')}
                    </Typography>
                    {billingStatus.cancelAtPeriodEnd && (
                      <Typography variant="caption" style={{ color: '#FF6347', fontSize: '11px' }}>
                        Cancelamento agendado
                      </Typography>
                    )}
                  </div>
                )}
              </div>

              {billingStatus.tradeLimit > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <Typography variant="body2" style={{ color: '#ffffff99', fontSize: '13px' }}>
                      Trades este mês
                    </Typography>
                    <Typography variant="body2" style={{
                      fontWeight: 600,
                      fontSize: '13px',
                      color: usagePercent > 90 ? '#FF6347' : usagePercent > 70 ? '#ffee58' : '#ffffffcc',
                    }}>
                      {billingStatus.tradeCount} / {billingStatus.tradeLimit}
                    </Typography>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#ffffff1a',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${usagePercent}%`,
                      height: '100%',
                      borderRadius: '3px',
                      background: usagePercent > 90 ? '#FF6347' : usagePercent > 70 ? '#ffee58' : '#4BDC4B',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {billingStatus.tradeLimit === -1 && (
                <Typography variant="body2" style={{ color: '#ffffff99', marginBottom: '16px', fontSize: '13px' }}>
                  Trades ilimitados — {billingStatus.tradeCount} trades este mês
                </Typography>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                {billingStatus.source !== 'kiwify' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    style={{ color: '#ffffffcc', borderColor: '#ffffff33', textTransform: 'none', fontSize: '13px' }}
                  >
                    {portalLoading ? <CircularProgress size={18} /> : 'Gerenciar assinatura'}
                  </Button>
                )}
                {billingStatus.source === 'kiwify' ? (
                  <Typography variant="body2" style={{ color: '#ffffff77', fontSize: '13px', alignSelf: 'center' }}>
                    Assinatura gerenciada via Kiwify
                  </Typography>
                ) : (
                  <Link to="/plans" style={{ textDecoration: 'none' }}>
                    <Button variant="outlined" size="small" style={{ color: '#ffee58', borderColor: '#ffee5844', textTransform: 'none', fontSize: '13px' }}>
                      Alterar plano
                    </Button>
                  </Link>
                )}
              </div>
            </>
          )}
        </Section>
      )}

      {/* Profile */}
      <Section>
        <SectionTitle>
          <PersonIcon style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '8px', marginTop: '-2px' }} />
          Perfil
        </SectionTitle>
        <TextField
          label="Nome"
          defaultValue={name}
          variant="filled"
          fullWidth
          onChange={e => setName(e.target.value)}
          style={{ marginBottom: '12px' }}
        />
        <TextField
          label="Telefone"
          value={phone}
          variant="filled"
          fullWidth
          placeholder="(00) 00000-0000"
          onChange={e => setPhone(phoneMask(e.target.value))}
          style={{ marginBottom: '12px' }}
        />
        <TextField
          label="Email"
          defaultValue={userState.user.email}
          variant="filled"
          fullWidth
          disabled
          style={{ marginBottom: '16px' }}
        />
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 0 16px' }} />
        <div style={{ display: 'flex', gap: '12px' }}>
          <TextField
            label="Meta mensal"
            defaultValue={goal}
            variant="filled"
            style={{ flex: 1 }}
            onChange={e => setGoal(parseInt(e.target.value))} // eslint-disable-line
          />
          <FormControl style={{ flex: 1 }}>
            <InputLabel variant="filled">Moeda</InputLabel>
            <Select
              variant="filled"
              value={currency}
              onChange={(e: any) => setCurrency(e.target.value)}
            >
              <MenuItem value={'BRL'}>BRL</MenuItem>
              <MenuItem value={'USD'}>USD</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <TextField
            label="Stop Win"
            defaultValue={stopWin}
            variant="filled"
            type="number"
            style={{ flex: 1 }}
            onChange={e => setStopWin(parseFloat(e.target.value) || 0)}
          />
          <TextField
            label="Stop Loss"
            defaultValue={stopLoss}
            variant="filled"
            type="number"
            style={{ flex: 1 }}
            onChange={e => setStopLoss(parseFloat(e.target.value) || 0)}
          />
        </div>
      </Section>

      {/* Password */}
      <Section>
        <SectionTitle>
          <LockIcon style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '8px', marginTop: '-2px' }} />
          Alterar senha
        </SectionTitle>
        <FormControl className={classes.formControl} variant="filled" style={{ marginBottom: '12px' }}>
          <InputLabel>Senha atual</InputLabel>
          <FilledInput
            type={currentPassword.showPassword ? 'text' : 'password'}
            value={currentPassword.value}
            onChange={e => setCurrentPassword({ ...currentPassword, value: e.target.value })}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setCurrentPassword({ ...currentPassword, showPassword: !currentPassword.showPassword })}
                  onMouseDown={e => e.preventDefault()}
                >
                  {currentPassword.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        <FormControl className={classes.formControl} variant="filled" style={{ marginBottom: '12px' }}>
          <InputLabel>Nova senha</InputLabel>
          <FilledInput
            type={newPassword.showPassword ? 'text' : 'password'}
            value={newPassword.value}
            onChange={e => setNewPassword({ ...newPassword, value: e.target.value })}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setNewPassword({ ...newPassword, showPassword: !newPassword.showPassword })}
                  onMouseDown={e => e.preventDefault()}
                >
                  {newPassword.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        <FormControl className={classes.formControl} variant="filled">
          <InputLabel>Confirme a nova senha</InputLabel>
          <FilledInput
            type={confirmNewPassword.showPassword ? 'text' : 'password'}
            value={confirmNewPassword.value}
            onChange={e => setconfirmNewPassword({ ...confirmNewPassword, value: e.target.value })}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setconfirmNewPassword({ ...confirmNewPassword, showPassword: !confirmNewPassword.showPassword })}
                  onMouseDown={e => e.preventDefault()}
                >
                  {confirmNewPassword.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Section>

      {/* Save button */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleSubmit()}
        disabled={!!loading}
        fullWidth
        style={{
          padding: '12px',
          fontWeight: 700,
          color: '#1E2141',
          borderRadius: '8px',
          fontSize: '15px',
        }}
      >
        {loading ? <CircularProgress size={24} /> : 'Salvar alterações'}
      </Button>
    </Container>
  );
}
